import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CommentThread } from '../components/core/comments/CommentThread';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { RankAvatarBadge } from '../components/RankAvatarBadge';
import { Separator } from '../components/ui/separator';
import { 
  getPost, 
  toggleLike as apiToggleLike, 
  getLikes, 
  checkUserLike,
  getUserPurchases,
  createPostPurchaseCheckout,
  validateDiscountCode,
  createPayPalOrder,
  getPayPalConfig
} from '../services/api';
import { Heart, MessageSquare, ArrowLeft, ExternalLink, Trash2, Lock, ShoppingCart, DollarSign, Loader2, FileText, Tag, X, CheckCircle, Play, Pencil, Image as ImageIcon } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import VideoPlayer from '../components/VideoPlayer';
import { linkifyText } from '../components/LinkifiedText';

// When a video is added in the editor, its thumbnail is auto-saved into post.images
// as the cover. We strip it out of the in-article gallery to avoid showing the same
// image twice (once as a static image, once as the video player poster).
const VIDEO_THUMBNAIL_HOSTS = ['img.youtube.com', 'vimeocdn.com', 'ytimg.com'];
const isVideoThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return VIDEO_THUMBNAIL_HOSTS.some((h) => url.includes(h));
};

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isOwner, isModerator, isReviewer } = useAuth();
  
  const [post, setPost] = useState(null);
  const [commentCount, setCommentCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [userPurchases, setUserPurchases] = useState({ purchased_posts: [], purchased_collections: [] });
  const [discountCode, setDiscountCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [paypalPurchasing, setPaypalPurchasing] = useState(false);

  const loadPostData = useCallback(async () => {
    try {
      setLoading(true);
      if (isReviewer) {
        const [postData, purchasesData] = await Promise.all([
          getPost(postId),
          getUserPurchases().catch(() => ({ purchased_posts: [], purchased_collections: [] }))
        ]);
        setPost(postData);
        setUserPurchases(purchasesData);
        return;
      }
      const [postData, likesData, userLikeData, purchasesData] = await Promise.all([
        getPost(postId),
        getLikes(postId),
        checkUserLike(postId).catch(() => ({ liked: false })),
        getUserPurchases().catch(() => ({ purchased_posts: [], purchased_collections: [] }))
      ]);
      
      setPost(postData);
      setLikeCount(likesData.count || 0);
      setIsLiked(userLikeData.liked);
      setUserPurchases(purchasesData);
    } catch (error) {
      if (error.response?.status === 403) {
        // Access denied - will show purchase prompt
        try {
          const postData = await getPost(postId);
          setPost(postData);
        } catch (e) {
          toast({
            title: 'Error',
            description: 'Failed to load case file',
            variant: 'destructive'
          });
        }
      } else if (error.response?.status === 404) {
        toast({
          title: 'Not Found',
          description: 'Case file not found',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [postId, isReviewer]);

  useEffect(() => {
    getPayPalConfig().then(() => setPaypalEnabled(true)).catch(() => {});
  }, []);

  useEffect(() => {
    loadPostData();
  }, [postId, loadPostData]);

  const handleLike = async () => {
    try {
      const result = await apiToggleLike(postId);
      setIsLiked(result.liked);
      setLikeCount(prev => result.liked ? prev + 1 : prev - 1);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update like',
        variant: 'destructive'
      });
    }
  };


  const handleBuyPost = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      setPurchasing(true);
      const result = await createPostPurchaseCheckout(postId);
      window.location.href = result.checkout_url;
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initiate purchase',
        variant: 'destructive'
      });
      setPurchasing(false);
    }
  };

  const handleBuyPostPayPal = async () => {
    if (!currentUser) { navigate('/login'); return; }
    setPaypalPurchasing(true);
    try {
      const result = await createPayPalOrder(postId, 'post');
      if (result.approval_url) {
        window.location.href = result.approval_url;
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create PayPal checkout', variant: 'destructive' });
      setPaypalPurchasing(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast({
        title: 'Enter Code',
        description: 'Please enter a discount code',
        variant: 'destructive'
      });
      return;
    }

    try {
      setValidatingCode(true);
      // Validate against a generic tier - the backend should work with post purchases too
      const result = await validateDiscountCode(discountCode, 'bronze');
      
      if (result.valid) {
        // Calculate the discount for this post price
        const originalPrice = post.price;
        let discountedPrice = originalPrice;
        
        if (result.discounted_price < result.original_price) {
          // Percentage discount - apply same percentage to post price
          const discountPercent = (result.original_price - result.discounted_price) / result.original_price;
          discountedPrice = Math.max(0.50, originalPrice * (1 - discountPercent));
        }
        
        setAppliedDiscount({
          code: result.code,
          originalPrice: originalPrice,
          discountedPrice: discountedPrice,
          savings: originalPrice - discountedPrice
        });
        toast({
          title: 'Discount Applied!',
          description: `You save $${(originalPrice - discountedPrice).toFixed(2)} with code ${result.code}`
        });
      }
    } catch (error) {
      toast({
        title: 'Invalid Code',
        description: error.response?.data?.detail || 'This discount code is not valid',
        variant: 'destructive'
      });
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleClearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto" />
          <p className="mt-4 text-amber-500/70 font-typewriter">Loading case file...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto text-amber-900/50 mb-4" />
          <h1 className="text-2xl font-headline text-amber-500 mb-4 tracking-wider">CASE FILE NOT FOUND</h1>
          <p className="text-gray-400 font-typewriter mb-6">This evidence file does not exist or has been removed.</p>
          <Button onClick={() => navigate('/feed')} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
            Back to Evidence Files
          </Button>
        </div>
      </div>
    );
  }

  // Check if this is a paid post and user doesn't own it
  const isPaidPost = post.isForSale && post.price > 0;
  const userOwnsPost = userPurchases.purchased_posts?.includes(post.id);
  const hasAccess = isOwner || isModerator || userOwnsPost || !isPaidPost;

  // Show purchase prompt for paid posts user doesn't own
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-black relative" data-testid="post-purchase-page">
        {/* Texture overlay */}
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        
        <div className="container mx-auto px-4 py-8 max-w-4xl relative z-10">
          <Button 
            variant="ghost" 
            className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20" 
            onClick={() => navigate('/feed')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Evidence Files
          </Button>

          <Card className="bg-gray-900/90 border-2 border-amber-900/30 overflow-hidden">
            {/* Preview Image */}
            {post.images && post.images.length > 0 && (
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={post.images[0]} 
                  alt={post.title}
                  className="w-full h-full object-cover grayscale-[30%] blur-sm"
                  style={{filter: 'sepia(0.2) contrast(1.05) blur(8px)'}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <Badge className="bg-amber-700/90 text-black font-headline tracking-wider">
                    CASE #{post.id?.slice(-4) || '0001'}
                  </Badge>
                </div>
              </div>
            )}

            <CardContent className="p-8 text-center">
              <Lock className="w-16 h-16 mx-auto text-amber-600 mb-6" />
              
              <h1 className="text-3xl font-headline text-amber-500 mb-4 tracking-wider">{post.title}</h1>
              
              <p className="text-gray-400 font-typewriter mb-6 max-w-lg mx-auto">
                {post.content?.substring(0, 200)}...
              </p>

              {/* Discount Code Section */}
              <div className="bg-gray-800/50 border border-amber-900/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <div className="flex items-center gap-2 mb-3 justify-center">
                  <Tag className="w-4 h-4 text-amber-500" />
                  <span className="font-headline text-amber-500 tracking-wider text-sm">HAVE A DISCOUNT CODE?</span>
                </div>
                
                {appliedDiscount ? (
                  <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-mono text-green-400 font-bold">{appliedDiscount.code}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleClearDiscount}
                        className="text-gray-400 hover:text-red-400 h-6 w-6 p-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-green-400 mt-2 font-typewriter">
                      You save <span className="font-bold">${appliedDiscount.savings.toFixed(2)}</span>!
                    </p>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="bg-gray-900 border-amber-900/50 focus:border-amber-600 font-mono uppercase flex-1 text-center"
                      data-testid="post-discount-code-input"
                    />
                    <Button 
                      onClick={handleApplyDiscount}
                      disabled={validatingCode || !discountCode.trim()}
                      className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                    >
                      {validatingCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'APPLY'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <div className={`border-2 rounded-lg p-6 mb-6 max-w-md mx-auto ${appliedDiscount ? 'bg-green-900/20 border-green-700/50' : 'bg-green-900/20 border-green-700/50'}`}>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <DollarSign className="w-8 h-8 text-green-500" />
                  {appliedDiscount ? (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-headline text-gray-500 line-through">${post.price?.toFixed(2)}</span>
                      <span className="text-4xl font-headline text-green-500">${appliedDiscount.discountedPrice.toFixed(2)}</span>
                    </div>
                  ) : (
                    <span className="text-4xl font-headline text-green-500">{post.price?.toFixed(2)}</span>
                  )}
                </div>
                <p className="text-gray-400 font-typewriter text-sm mb-4">
                  Get full access to this exclusive case file
                </p>
                <Button 
                  onClick={handleBuyPost}
                  disabled={purchasing}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-headline tracking-wider text-lg py-6"
                  data-testid="buy-post-detail-btn"
                >
                  {purchasing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 mr-2" />
                  )}
                  {appliedDiscount ? `PURCHASE - SAVE $${appliedDiscount.savings.toFixed(2)}` : 'PURCHASE THIS CASE FILE'}
                </Button>
                {paypalEnabled && (
                  <Button
                    variant="outline"
                    onClick={handleBuyPostPayPal}
                    disabled={paypalPurchasing}
                    className="w-full border-blue-800/50 text-blue-400 hover:bg-blue-900/20 font-typewriter text-sm mt-2"
                    data-testid="buy-post-paypal-btn"
                  >
                    {paypalPurchasing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.603c-.564 0-1.04.408-1.13.964L7.076 21.337z"/></svg>
                    )}
                    PAY WITH PAYPAL
                  </Button>
                )}
              </div>

              <p className="text-gray-500 font-typewriter text-sm">
                One-time purchase • Instant access • Stripe or PayPal
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Locked post view - FOMO teaser for higher tier content
  if (post.locked) {
    const TIER_NAMES = { 0: 'Free', 1: 'Rookie', 2: 'Detective', 3: 'Commissioner' };
    const requiredTier = TIER_NAMES[post.minimumTierLevel] || 'Premium';
    
    return (
      <motion.div 
        className="min-h-screen bg-black relative" 
        data-testid="post-detail-locked"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        <div className="container mx-auto px-4 py-8 max-w-4xl relative z-10">
          <Button 
            variant="ghost" 
            className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20" 
            onClick={() => navigate('/feed')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Evidence Files
          </Button>

          <Card className="bg-gray-900/90 border-2 border-amber-600/40">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 rounded-full bg-amber-900/30 border-2 border-amber-600/50 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-amber-500" />
                </div>
                <h1 className="text-3xl font-headline text-amber-500 tracking-wider mb-2">{post.title}</h1>
                <p className="text-sm text-gray-500 font-typewriter">{formatDate(post.publishedAt)}</p>
              </div>
              
              <div className="bg-black/50 border border-amber-900/40 rounded-lg p-6 mb-6 max-w-md mx-auto">
                <p className="text-amber-400 font-headline tracking-wider text-lg mb-2">CLASSIFIED CASE FILE</p>
                <p className="text-gray-400 font-typewriter text-sm mb-4">
                  This evidence requires <span className="text-amber-500 font-bold">{requiredTier}</span> clearance level to access.
                </p>
                <Button 
                  onClick={() => navigate('/tiers')}
                  className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider px-8"
                  data-testid="upgrade-tier-btn"
                >
                  UPGRADE CLEARANCE
                </Button>
              </div>
              
              <p className="text-gray-600 font-typewriter text-xs">
                Upgrade your membership to unlock this and other exclusive case files
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    );
  }

  return (
    <>
    <motion.div 
      className="min-h-screen bg-black relative" 
      data-testid="post-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Button 
            variant="ghost" 
            className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20" 
            onClick={() => navigate('/feed')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Evidence Files
          </Button>
        </motion.div>

        {/* Post Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="mb-6 bg-gray-900/90 border-2 border-amber-900/30">
          <CardContent className="p-6 md:p-8">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-amber-700/50">
                  <AvatarFallback className="bg-amber-900/50 text-amber-500 font-headline">D</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-headline text-amber-500 tracking-wide">DARIA</p>
                  <p className="text-sm text-gray-500 font-typewriter">{formatDate(post.publishedAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isOwner && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-700/50 text-amber-500 hover:bg-amber-900/30 hover:text-amber-400 font-headline tracking-wider"
                    onClick={() => navigate(`/admin/posts/${postId}/edit`)}
                    data-testid="edit-post-btn"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    EDIT
                  </Button>
                )}
                {userOwnsPost && (
                  <Badge className="bg-amber-600 text-black font-headline tracking-wider">PURCHASED</Badge>
                )}
                {post.featured && (
                  <Badge className="bg-red-700 font-headline tracking-wider">FEATURED</Badge>
                )}
              </div>
            </div>

            {/* Case Number Tab */}
            <div className="inline-block mb-4">
              <div className="bg-amber-700/90 text-black px-4 py-1 font-headline tracking-wider text-sm">
                CASE #{post.id?.slice(-4) || '0001'}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-headline text-gray-100 mb-6 tracking-wide">{post.title}</h1>

            {/* Images — when the post has video(s), treat images[0] as cover-only (already shown in feed card / locked preview) and skip it here so the player is the visual focus. Additional images[1..N] still render. */}
            {(() => {
              const hasVideos = post.vimeoEmbedUrls && post.vimeoEmbedUrls.length > 0;
              const all = post.images || [];
              let galleryImages;
              if (hasVideos) {
                // Drop images[0] (the cover) AND any video-thumbnail-host images anywhere in the list.
                galleryImages = all.slice(1).filter((img) => !isVideoThumbnailUrl(img));
                // Edge case: if images[0] wasn't a cover (e.g. legacy posts where images[0] is a real evidence photo and there's no separate cover), still skip it for consistency.
              } else {
                galleryImages = all;
              }
              if (galleryImages.length === 0) return null;
              return (
                <div className="mb-6 space-y-4">
                  {galleryImages.map((img, idx) => (
                    <img 
                      key={`img-${idx}-${img.slice(-20)}`} 
                      src={img} 
                      alt={`Evidence ${idx + 1}`} 
                      className="w-full rounded-lg border-2 border-amber-900/30 cursor-pointer hover:border-amber-600/60 transition-colors"
                      style={{filter: 'sepia(0.2) contrast(1.05)'}}
                      onClick={() => setLightboxImage(img)}
                      data-testid={`post-image-${idx}`}
                    />
                  ))}
                </div>
              );
            })()}

            {/* Vimeo Videos */}
            {post.vimeoEmbedUrls && post.vimeoEmbedUrls.length > 0 && (
              <div className="mb-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Play className="w-5 h-5 text-amber-500" />
                  <h3 className="font-headline text-amber-500 tracking-wider">VIDEO EVIDENCE</h3>
                </div>
                {post.vimeoEmbedUrls.map((url, idx) => (
                  <div key={`video-${idx}-${url.slice(-20)}`} className="relative">
                    <VideoPlayer 
                      url={url} 
                      className="rounded-lg border-2 border-amber-900/30"
                    />
                    {post.vimeoEmbedUrls.length > 1 && (
                      <Badge className="absolute top-2 left-2 bg-black/70 font-typewriter text-xs z-10">
                        Video #{idx + 1}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Preview-only accounts: non-playable video frames + photo count */}
            {post.previewMode && (post.hasVideo || post.imageCount > 1) && (
              <div className="mb-6 space-y-3" data-testid="post-preview-placeholders">
                {post.hasVideo && Array.from({ length: Math.max(1, post.videoCount || 1) }).map((_, idx) => (
                  <div
                    key={`preview-video-${idx}`}
                    className="relative aspect-video rounded-lg border-2 border-amber-900/30 bg-gradient-to-br from-gray-900 to-black flex flex-col items-center justify-center overflow-hidden"
                    data-testid={`preview-video-${idx}`}
                  >
                    <div className="absolute inset-0 opacity-10 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(251,191,36,0.4)_10px,rgba(251,191,36,0.4)_11px)]" />
                    <div className="w-16 h-16 rounded-full bg-amber-600/80 flex items-center justify-center shadow-lg relative z-10">
                      <Play className="w-8 h-8 text-black ml-1" fill="currentColor" />
                    </div>
                    <span className="mt-3 font-headline tracking-wider text-amber-500 text-sm relative z-10">
                      VIDEO EVIDENCE{(post.videoCount || 1) > 1 ? ` · ${idx + 1} OF ${post.videoCount}` : ''}
                    </span>
                  </div>
                ))}
                {post.imageCount > 1 && (
                  <div className="flex items-center gap-2 text-gray-500 font-typewriter text-sm" data-testid="preview-photo-count">
                    <ImageIcon className="w-4 h-4 text-amber-600" />
                    {post.imageCount} photos in this case file
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="prose max-w-none mb-6">
              <p className="text-lg leading-relaxed whitespace-pre-wrap text-gray-300 font-typewriter">{linkifyText(post.content)}</p>
            </div>

            {/* Private Content - only shown to purchasers & owner/mod */}
            {post.privateContent && (
              <div className="mb-6 p-5 bg-amber-900/10 border-2 border-amber-600/40 rounded-lg" data-testid="private-content-section">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-5 h-5 text-amber-500" />
                  <h3 className="font-headline text-amber-500 tracking-wider">EXCLUSIVE CONTENT</h3>
                  <Badge className="bg-amber-600 text-black font-headline tracking-wider text-xs">UNLOCKED</Badge>
                </div>
                <div className="text-gray-300 font-typewriter whitespace-pre-wrap leading-relaxed">
                  {linkifyText(post.privateContent)}
                </div>
              </div>
            )}

            {/* External Links */}
            {post.externalLinks && post.externalLinks.length > 0 && (
              <div className="mb-6 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                <h3 className="font-headline text-amber-500 mb-3 tracking-wider">RELATED EVIDENCE</h3>
                <div className="space-y-2">
                  {post.externalLinks.map((link, idx) => (
                    <a 
                      key={`link-${idx}-${link.slice(-20)}`} 
                      href={link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-typewriter text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {link}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {!post.previewMode && (
            <div className="flex items-center gap-4 pt-4 border-t border-amber-900/30">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  variant="ghost" 
                  onClick={handleLike}
                  className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Heart className={`w-5 h-5 transition-all ${isLiked ? 'fill-red-500 text-red-500 scale-110' : ''}`} />
                  <span>{likeCount} Likes</span>
                </Button>
              </motion.div>
              <div className="flex items-center gap-2 text-gray-500">
                <MessageSquare className="w-5 h-5" />
                <span>{commentCount} Comments</span>
              </div>
            </div>
            )}
          </CardContent>
        </Card>
        </motion.div>

        {/* Comments Section */}
        {!post.previewMode && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
          <CommentThread postId={postId} me={currentUser} canModerate={isOwner || isModerator} onCountChange={setCommentCount} />
        </motion.div>
        )}
      </div>
    </motion.div>

    {/* Image Lightbox */}
    {lightboxImage && (
      <div 
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
        onClick={() => setLightboxImage(null)}
        data-testid="image-lightbox"
      >
        <button
          className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox-close"
        >
          <X className="w-8 h-8" />
        </button>
        <img 
          src={lightboxImage} 
          alt="Full size" 
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: 'default' }}
        />
      </div>
    )}
    </>
  );
};

export default PostDetailPage;
